import stateApi from '@/api/stateApi';
import ApplyIndexesDialog from '@/components/applyIndexesDialog';
import ApplyIndexJobDetails from '@/components/applyIndexJobDetails';
import IndexDetails from '@/components/indexDetails';
import { jobStateIcons } from '@/components/jobStateIcons';
import { LoadingIndicator } from '@/components/loadingIndicator';
import useAuth from '@/hooks/useAuth';
import useClock from '@/hooks/useClock';
import { appShellHeaderHeight } from '@/theme/theme';
import { Badge, Button, Group, HoverCard, Progress, Stack } from '@mantine/core';
import { modals } from '@mantine/modals';
import { createFileRoute } from '@tanstack/react-router';
import calculateTasks from 'lib/calculateTasks';
import { formatDate, formatMilliseonds } from 'lib/format/timeFormat';
import type { IndexTask } from 'lib/models/indexes';
import { JobState } from 'lib/models/job';
import { useCallback, useState } from 'react';
import { selectFilter, Table, textFilter, type Id, type TableProps } from 'schummar-table';

export const Route = createFileRoute('/jobs/')({
  component: RouteComponent,
});

function RouteComponent() {
  const { open, planned } = stateApi.state.useStore(useCallback(calculateTasks, []));
  const ready = stateApi.state.useStore((x) => x.actual.ready);

  if (!ready) {
    return (
      <Group flex={1} justify="center" align="center">
        <LoadingIndicator />
      </Group>
    );
  }

  return (
    <Stack
      css={{
        padding: 'var(--mantine-spacing-md)',

        'h2:not(:first-of-type)': {
          marginTop: 'var(--mantine-spacing-xl)',
        },
      }}
    >
      <OpenTasks tasks={open} />
      <PlannedTasks tasks={planned} />
    </Stack>
  );
}

function OpenTasks({ tasks }: { tasks: IndexTask[] }) {
  const [selection, setSelection] = useState(new Set<Id>());
  const count = selection.size || tasks.length;
  const { canWrite } = useAuth();

  async function schedule() {
    const indexes = tasks.filter((task) => selection.size === 0 || selection.has(task.id));

    modals.open({
      title: 'Schedule Index Builds',
      children: <ApplyIndexesDialog tasks={indexes} />,
    });
  }

  return (
    <>
      <h2>Open jobs</h2>

      <Group>
        {canWrite && (
          <Button onClick={schedule} disabled={count === 0}>
            Schedule {count}
          </Button>
        )}
      </Group>

      <TaskTable
        items={tasks}
        columns={(col) => [
          col((item) => item.index?.db, {
            id: 'db',
            header: 'DB',
            filter: selectFilter(),
          }),

          col((item) => item.index?.coll, {
            id: 'collection',
            header: 'Collection',
            filter: selectFilter(),
          }),

          col((item) => item.index?.name, {
            id: 'index',
            header: 'Index',
            filter: textFilter(),
          }),

          col((item) => ({ toCreate: item.toCreate?.length, toUpdate: item.toUpdate?.length, toDelete: item.toDelete?.length }), {
            id: 'task',
            header: 'Task summary',
            renderCell: (task) => (
              <Group>
                {!!task.toCreate && (
                  <Badge color="blue.4" size="xs">
                    {task.toCreate} to create
                  </Badge>
                )}
                {!!task.toUpdate && (
                  <Badge color="yellow.4" size="xs">
                    {task.toUpdate} to update
                  </Badge>
                )}
                {!!task.toDelete && (
                  <Badge color="red.4" size="xs">
                    {task.toDelete} to delete
                  </Badge>
                )}
              </Group>
            ),
            disableSort: true,
          }),
        ]}
        selection={selection}
        onSelectionChange={setSelection}
        defaultSort={[
          { columnId: 'db', direction: 'asc' },
          { columnId: 'collection', direction: 'asc' },
          { columnId: 'index', direction: 'asc' },
        ]}
        wrapRow={(props, item) => (
          <HoverCard openDelay={500}>
            <HoverCard.Target>
              <div {...props} />
            </HoverCard.Target>

            <HoverCard.Dropdown
              maw="min(800px, 95dvw)"
              mah={400}
              css={{
                overflow: 'auto',
              }}
            >
              <Stack>{item.collection && item.index && <IndexDetails collection={item.collection} index={item.index} hideCharts />}</Stack>
            </HoverCard.Dropdown>
          </HoverCard>
        )}
      />
    </>
  );
}

function PlannedTasks({ tasks }: { tasks: IndexTask[] }) {
  const [selection, setSelection] = useState(new Set<Id>());
  const now = useClock();

  return (
    <>
      <h2>Planned jobs</h2>

      <TaskTable
        items={tasks}
        enableSelection={false}
        columns={(col) => [
          col((item) => item.job?.state, {
            id: 'state',
            header: 'State',
            renderCell(state, item) {
              if (item.job?.lock) {
                const completed = item.job?.progress?.completed ?? 0;
                const errored = item.job?.progress?.errored ?? 0;
                const progress = completed + errored;

                return (
                  <Progress.Root css={{ width: '100%' }} title={`${Math.round(progress * 100)}%`}>
                    <Progress.Section value={completed * 100} animated={!!item.job?.lock} />
                    <Progress.Section value={errored * 100} color="red" animated={!!item.job?.lock} />
                  </Progress.Root>
                );
              }

              return <Group gap="xs">{state && jobStateIcons[state]}</Group>;
            },
            filter: selectFilter({ options: JobState.options }),
          }),

          col((item) => item.job?.data.actual?.db ?? item.job?.data.db, {
            id: 'db',
            header: 'DB',
            filter: selectFilter(),
          }),

          col((item) => item.job?.data.actual?.coll ?? item.job?.data.coll, {
            id: 'collection',
            header: 'Collection',
            filter: selectFilter(),
          }),

          col((item) => item.job?.data.name, {
            id: 'index',
            header: 'Index',
            filter: textFilter(),
          }),

          col((item) => item.job?.nextRun, {
            id: 'scheduled',
            header: 'Scheduled',
            renderCell(date) {
              return date ? formatDate(date) : '-';
            },
          }),

          col((item) => (item.job?.state === 'completed' || item.job?.lock) && item.job?.nextRun, {
            id: 'duration',
            header: 'Duration',
            renderCell: (t, item) => {
              const duration = t && (item.job?.finishedOn ?? now).getTime() - t.getTime();
              return duration ? formatMilliseonds(duration) : '-';
            },
          }),

          col((item) => (item.job?.attempt ?? 0) + 1, {
            id: 'attempt',
            header: 'Attempt',
          }),

          col((item) => item.job?.data.triggeredBy, {
            id: 'triggeredBy',
            header: 'Triggered By',
            renderCell(value) {
              return value?.name;
            },
            filter: textFilter(),
          }),

          col((item) => item.job?.data.triggeredOn, {
            id: 'triggeredOn',
            header: 'Triggered On',
            renderCell(value) {
              return value ? formatDate(value) : '-';
            },
          }),
        ]}
        selection={selection}
        onSelectionChange={setSelection}
        defaultSort={[{ columnId: 'scheduled', direction: 'desc' }]}
        wrapRow={(props, index) => (
          <HoverCard openDelay={500}>
            <HoverCard.Target>
              <div {...props} />
            </HoverCard.Target>

            <HoverCard.Dropdown
              maw="min(800px, 95dvw)"
              mah={400}
              css={{
                overflow: 'auto',
              }}
            >
              <Stack>
                <ApplyIndexJobDetails job={index.job} />
              </Stack>
            </HoverCard.Dropdown>
          </HoverCard>
        )}
      />
    </>
  );
}

function TaskTable(props: Partial<TableProps<IndexTask>>) {
  return (
    <Table<IndexTask>
      id="id"
      parentId="parentId"
      columns={[]}
      enableColumnSelection={false}
      defaultSort={[{ columnId: 'nextRun', direction: 'desc' }]}
      stickyHeader={{ top: appShellHeaderHeight }}
      styles={{
        row: {
          '&:hover': {
            backgroundColor: 'var(--mantine-color-selection-6)',
          },
        },
      }}
      {...props}
    />
  );
}
